import { Formik, Form as FormikForm, FormikHelpers, FormikProps } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Row, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import InputText from "../../../components/inputs/inputText";
import ScrollToFieldError from "../../../components/ScrollToFieldError";
import { InfoCircle } from "react-bootstrap-icons";
import { validationSchemaSeoSettings } from "../../../validations/whiteLabel";
import InputTextArea from "../../../components/inputs/inputTextArea";
import {
  useCreateOrUpdateWhiteLabelSeoSettingsMutation,
  useOrganizerWhiteLabelQuery,
} from "../../../qraphql/whiteLabel/whiteLabel.hooks";
import ApolloError from "../../../components/apolloError";
import CustomToast from "../../../components/tooltips/CustomToast";
import { useReactiveVar } from "@apollo/client";
import {
  whiteLabelJRActive,
  whiteLabelJRStep,
} from "../../../qraphql/globalVar/state";

interface ISeoSettingsForm {
  title: string;
  description: string;
  keywords: string;
}

export default function SeoSettings() {
  const { t } = useTranslation();
  const formRef = useRef<FormikProps<ISeoSettingsForm>>(null);
  const [
    createOrUpdateWhiteLabelSeoSettings,
    {
      loading: createOrUpdateWhiteLabelSeoSettingsLoading,
      error: createOrUpdateWhiteLabelSeoSettingsError,
    },
  ] = useCreateOrUpdateWhiteLabelSeoSettingsMutation();
  const { data: whiteLabelInfo, loading: whiteLabelInfoLoading } =
    useOrganizerWhiteLabelQuery();

  const [isShowToast, setIsShowToast] = useState(false);

  const whiteLabelJRActiveValue = useReactiveVar(whiteLabelJRActive);

  useEffect(() => {
    if (whiteLabelInfo) {
      const whiteLabelConfig =
        whiteLabelInfo.organizer.whiteLabel?.whiteLabelConfig;
      formRef.current?.setValues({
        title: whiteLabelConfig?.metaTitle || "",
        description: whiteLabelConfig?.metaDescription || "",
        keywords: whiteLabelConfig?.keywords || "",
      });
    }
  }, [whiteLabelInfo]);

  const onSubmit = async (
    values: ISeoSettingsForm,
    { setSubmitting }: FormikHelpers<ISeoSettingsForm>
  ) => {
    await createOrUpdateWhiteLabelSeoSettings({
      variables: {
        input: {
          metaTitle: values.title,
          metaDescription: values.description,
          keywords: values.keywords,
        },
      },
      onCompleted: () => {
        setIsShowToast(true);
        if (whiteLabelJRActiveValue) {
          whiteLabelJRStep(4);
          (
            document.querySelector(".social-links-tab") as HTMLElement | null
          )?.click();
        }
      },
    });
    setSubmitting(false);
  };

  const initialValues: ISeoSettingsForm = {
    title: "",
    description: "",
    keywords: "",
  };

  if (whiteLabelInfoLoading) {
    return (
      <div
        className="d-flex align-items-center justify-content-center"
        style={{ height: 200 }}
      >
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div id="seo-info">
      <Formik
        innerRef={formRef}
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchemaSeoSettings(t)}
        enableReinitialize
      >
        {({ isSubmitting, isValid, values }) => {
          return (
            <FormikForm>
              <ScrollToFieldError />
              <div className="bg-success-bg text-success p-3 rounded d-flex mb-3 fs-7 align-items-baseline">
                <div>
                  <InfoCircle className="pe-2" height={24} width={24} />
                </div>
                {t("whiteLabel.seoPageInfo")}
              </div>
              <InputText
                name="title"
                className="mb-0"
                placeholder={t("whiteLabel.Title")}
                popoverText={t("whiteLabel.seoTitleInfo")}
                isValidateImmediate
              />
              <div className="text-end text-info fs-7 mb-3 mt-1">
                {values.title.length}/60
              </div>

              <InputTextArea
                placeholder={t("whiteLabel.Description")}
                name="description"
                height={100}
                popoverText={t("whiteLabel.seoDescrInfo")}
                isValidateImmediate
              />
              <div className="text-end text-info fs-7 mb-3 mt-1">
                {values.description.length}/160
              </div>

              <InputText
                name="keywords"
                className="mb-5"
                placeholder={t("whiteLabel.Keywords")}
                popoverText={t("whiteLabel.seoKeyInfo")}
                isValidateImmediate
              />

              <Row className="mb-3">
                <Col sm={4}>
                  <Button
                    variant="success"
                    className="fw-bold w-100 py-2"
                    type="submit"
                    disabled={
                      isSubmitting ||
                      !isValid ||
                      createOrUpdateWhiteLabelSeoSettingsLoading
                    }
                  >
                    {t("firstStep.save")}
                    {createOrUpdateWhiteLabelSeoSettingsLoading && (
                      <Spinner
                        animation="border"
                        role="status"
                        size="sm"
                        className="ms-2"
                      />
                    )}
                  </Button>
                </Col>
              </Row>
              <ApolloError
                networkError={
                  createOrUpdateWhiteLabelSeoSettingsError?.networkError
                }
              />
              <CustomToast
                isShow={isShowToast}
                handleClose={() => setIsShowToast(false)}
                message={t("whiteLabel.Changes Saved")}
              />
            </FormikForm>
          );
        }}
      </Formik>
    </div>
  );
}
